import Button from 'components/form/Button';
import CheckBox from 'components/form/CheckBox';
import Label from 'components/form/Label';
import TextInput from 'components/form/TextInput';
import ToggleSwitch from 'components/form/ToggleSwitch';
import Card from 'components/Layout/Card';
import Profile from 'components/Layout/Profile';
import Table from 'components/Layout/Table';
import { isEqual } from 'lodash';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  fetchRBACUserPermission,
  throwError,
  throwSuccess,
  updateRBACUserPermission,
} from 'store/slices';
import {
  getCountryCode,
  getDataFromLocalStorage,
  objectToFormData,
  titleCaseString,
} from 'utils/helpers';
import Dropdown from 'components/form/Dropdown';
import { userRole } from 'utils/constants';
import RequestVerificationModal from 'pages/Common/RequestRaisedAndReceived/Common/RequestVerificationModal';

const RBACUserListDetail = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const { userDetails } = useSelector((state) => ({
    userDetails: state.student.userDetails,
  }));
  const { user_type } = getDataFromLocalStorage();
  const { type, listType, userId, userType } = params;
  const [isLoading, setIsLoading] = useState(true);
  const [sidebarMenuList, setSidebarMenuList] = useState([]);
  const [userData, setUserData] = useState({});
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [isHide, setIsHide] = useState(true);
  const [initialData, setInitialData] = useState({});
  const [errors, setErrors] = useState({});
  const loginUserRole = getDataFromLocalStorage('user_type');

  const [selectAllPermissions, setSelectAllPermissions] = useState({
    view: false,
    create: false,
    update: false,
    delete: false,
  });

  const [switchUserData, setSwitchUserData] = useState({
    showConfirmation: false,
    newUserType: null,
  });

  // console.log('✌️userData --->', userData);
  const getRBACUserPermissionDetail = async () => {
    let payload = { user_id: userId, user_type: userType };
    if (listType === 'all') {
      delete payload?.user_id;
    }
    const response = await dispatch(
      fetchRBACUserPermission(objectToFormData(payload))
    );
    let resData = {};
    let resList = [];
    if (response?.data?.role_data) {
      resData = response?.data?.user_data || {};
      resList = response?.data?.role_data || [];
      setInitialData(response?.data);
    }
    setUserData(resData);
    setSidebarMenuList(resList);
    setIsLoading(false);
  };

  const handleCheck = (data) => {
    const { parentId, permissionObj, checkBoxType, checkValue } = data;
    const updatedList = sidebarMenuList?.map((elem) => {
      if (elem?.id === parentId) {
        return {
          ...elem,
          sub_permission: {
            ...permissionObj,
            [checkBoxType]: checkValue ? '1' : '0',
          },
        };
      }
      return elem;
    });
    setSidebarMenuList(updatedList);
    setIsHide(
      isEqual(
        JSON.stringify(initialData?.role_data),
        JSON.stringify(updatedList)
      )
    );
  };

  const handleToggle = () => {
    let updatedData = userData;
    updatedData = {
      ...updatedData,
      data_masked: updatedData?.data_masked === '0' ? '1' : '0',
    };
    setUserData(updatedData);
    setIsHide(
      isEqual(
        JSON.stringify(initialData?.user_data?.data_masked),
        JSON.stringify(updatedData?.data_masked)
      )
    );
  };

  const handleExportDetailsToggle = () => {
    let updatedData = userData;
    updatedData = {
      ...updatedData,
      export_status: updatedData?.export_status === '0' ? '1' : '0',
    };
    setUserData(updatedData);
    setIsHide(
      isEqual(
        JSON.stringify(initialData?.user_data?.export_status),
        JSON.stringify(updatedData?.export_status)
      )
    );
  };

  const handleSwitchUserChange = (e) => {
    const { value } = e.target;
    if (value === userType) return;

    setSwitchUserData({
      showConfirmation: false,
      newUserType: value,
    });
    setIsHide(false);
  };

  const handleCallToggle = () => {
    let updatedData = userData;

    updatedData = {
      ...updatedData,
      call_integration: updatedData?.call_integration === '0' ? '1' : '0',
    };
    setUserData(updatedData);
    setIsHide(
      isEqual(
        JSON.stringify(initialData?.user_data?.call_integration),
        JSON.stringify(updatedData?.call_integration)
      )
    );

    // Clear error when toggle is switched off
    if (updatedData?.call_integration === '0') {
      setErrors((prev) => ({ ...prev, call_hippo_agent_id: '' }));
      setUserData((prev) => ({ ...prev, call_hippo_agent_id: '' }));
    }
  };

  // Update the validateForm function
  const validateForm = () => {
    const newErrors = {};
    if (
      userData?.call_integration === '1' &&
      !userData?.call_hippo_agent_id?.trim()
    ) {
      newErrors.call_hippo_agent_id =
        'Agent ID is required when Call Integration is enabled';
    }

    // Add validation for phone number
    if (
      userData?.call_integration === '1' &&
      !userData?.call_hippo_agent_phone_no?.trim()
    ) {
      newErrors.call_hippo_agent_phone_no =
        'Agent Phone No is required when Call Integration is enabled';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAgentIdChange = (e) => {
    const { value } = e.target;
    setUserData((prev) => ({
      ...prev,
      call_hippo_agent_id: value,
    }));
    setIsHide(false);

    // Clear error when user starts typing
    if (value.trim()) {
      setErrors((prev) => ({ ...prev, call_hippo_agent_id: '' }));
    }
  };

  // Add this handler function
  const handleAgentPhoneNoChange = (e) => {
    const { value } = e.target;
    setUserData((prev) => ({
      ...prev,
      call_hippo_agent_phone_no: value,
    }));
    setIsHide(false);

    // Clear error when user starts typing
    if (value.trim()) {
      setErrors((prev) => ({ ...prev, call_hippo_agent_phone_no: '' }));
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }
    if (
      loginUserRole === '0' &&
      switchUserData?.newUserType &&
      !switchUserData?.showConfirmation
    ) {
      setSwitchUserData((prev) => ({
        ...prev,
        showConfirmation: true,
      }));
      return;
    }

    setIsSubmitLoading(true);
    let payload = {
      permission_data: JSON.stringify(sidebarMenuList),
      is_masked: userData?.data_masked,
      export_status: userData?.export_status,
      calling_integration: userData?.call_integration,
      call_hippo_agent_id: userData?.call_hippo_agent_id,
      call_hippo_agent_phone_no: userData?.call_hippo_agent_phone_no,
      user_id: userId,
    };

    if (switchUserData.newUserType) {
      payload.swith_user_to = switchUserData.newUserType;
      payload.current_role = userType;
    }

    if (listType === 'all') {
      delete payload?.user_id;
    }
    const response = await dispatch(
      updateRBACUserPermission(objectToFormData(payload))
    );
    if (response?.status === 200) {
      dispatch(throwSuccess(response?.message));
      getRBACUserPermissionDetail();
      setIsHide(true);
      setSwitchUserData({
        showConfirmation: false,
        newUserType: null,
      });
    } else {
      dispatch(throwError({ message: response?.messsage }));
    }
    setIsSubmitLoading(false);
  };

  const handleConfirmUserTypeChange = () => {
    setSwitchUserData((prev) => ({
      ...prev,
      showConfirmation: false,
    }));
    handleSubmit();
  };

  const handleCancelUserTypeChange = () => {
    setSwitchUserData({
      showConfirmation: false,
      newUserType: null,
    });
    setUserData((prev) => ({
      ...prev,
      user_type: userType,
    }));
  };

  useEffect(() => {
    getRBACUserPermissionDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectAllChange = (permissionType) => {
    const isAllSelected = selectAllPermissions[permissionType];
    const newValue = !isAllSelected ? '1' : '0';

    const updatedList = sidebarMenuList.map((item) => ({
      ...item,
      sub_permission: {
        ...item.sub_permission,
        [permissionType]: newValue,
      },
    }));

    setSidebarMenuList(updatedList);
    setIsHide(
      isEqual(
        JSON.stringify(initialData?.role_data),
        JSON.stringify(updatedList)
      )
    );
  };

  useEffect(() => {
    const permissions = ['view', 'create', 'update', 'delete'];
    const updatedSelectAll = {};

    permissions.forEach((perm) => {
      updatedSelectAll[perm] = sidebarMenuList.every(
        (item) => item.sub_permission[perm] === '1'
      );
    });

    setSelectAllPermissions(updatedSelectAll);
  }, [sidebarMenuList]);

  const header = [
    {
      isSearch: false,
      searchInputName: 'name',
      title: 'S.No',
    },
    {
      isSearch: true,
      searchInputName: 'name',
      title: <div className="text-nowrap">Events Name</div>,
    },
    {
      searchInputName: 'name',
      title: <div className="text-nowrap">View</div>,
      isCheckBox: true,
      accessKey: 'view',
    },
    {
      searchInputName: 'name',
      title: <div className="text-nowrap">Create</div>,
      isCheckBox: true,
      accessKey: 'create',
    },
    {
      searchInputName: 'name',
      title: <div className="text-nowrap">Update</div>,
      isCheckBox: true,
      accessKey: 'update',
    },
    {
      searchInputName: 'name',
      title: <div className="text-nowrap">Delete</div>,
      isCheckBox: true,
      accessKey: 'delete',
    },
  ];

  const rowData = [];
  sidebarMenuList?.forEach((elem, index) => {
    const { id, permission_name, sub_permission } = elem;
    const {
      view: viewVal,
      create: createVal,
      update: updateVal,
      delete: deleteVal,
    } = sub_permission || {};
    let obj = [
      {
        value: <span>{index + 1}</span>,
      },
      {
        value: (
          <span
            className={`text-nowrap text-14-400-18`}
          >{`${permission_name}`}</span>
        ),
      },
      {
        value: (
          <div className="d-flex justify-content-center">
            <CheckBox
              type="ACTIVE"
              onClick={(e) => {
                const payload = {
                  parentId: id,
                  permissionObj: sub_permission,
                  checkBoxType: 'view',
                  checkValue: viewVal !== '1',
                };
                handleCheck(payload);
              }}
              isChecked={viewVal === '1'}
            />
          </div>
        ),
      },
      {
        value: (
          <div className="d-flex justify-content-center">
            <CheckBox
              type="ACTIVE"
              onClick={(e) => {
                const payload = {
                  parentId: id,
                  permissionObj: sub_permission,
                  checkBoxType: 'create',
                  checkValue: createVal !== '1',
                };
                handleCheck(payload);
              }}
              isChecked={createVal === '1'}
            />
          </div>
        ),
      },
      {
        value: (
          <div className="d-flex justify-content-center">
            <CheckBox
              type="ACTIVE"
              onClick={(e) => {
                const payload = {
                  parentId: id,
                  permissionObj: sub_permission,
                  checkBoxType: 'update',
                  checkValue: updateVal !== '1',
                };
                handleCheck(payload);
              }}
              isChecked={updateVal === '1'}
            />
          </div>
        ),
      },
      {
        value: (
          <div className="d-flex justify-content-center">
            <CheckBox
              type="ACTIVE"
              onClick={(e) => {
                const payload = {
                  parentId: id,
                  permissionObj: sub_permission,
                  checkBoxType: 'delete',
                  checkValue: deleteVal !== '1',
                };
                handleCheck(payload);
              }}
              isChecked={deleteVal === '1'}
            />
          </div>
        ),
      },
    ];
    rowData.push({ data: obj });
  });

  const {
    profile_image,
    name,
    email_id,
    phone_number,
    date_of_joining,
    member_id,
    data_masked,
    call_integration,
    country_code,
    export_status,
  } = userData || {};

  const userLabel = `${titleCaseString(listType)} ${titleCaseString(
    type
  )?.replace('-', ' ')}`;

  const {
    // view: viewAccess,
    // create: createAccess,
    update: updateAccess,
    // delete: deleteAccess,
  } =
    userDetails?.role_data?.find((item) => item?.permission_name === 'Settings')
      ?.sub_permission || {};

  const filteredOptions = userRole?.filter(
    (item) => item.id !== userType && item.id !== loginUserRole
  );

  return (
    <>
      {switchUserData?.showConfirmation && (
        <>
          <RequestVerificationModal
            handleConfirmation={handleConfirmUserTypeChange}
            onHide={handleCancelUserTypeChange}
            title="You want to share the switch user type request to super admin"
          />
        </>
      )}
      <Card className="rbac-user-type-container cps-24 cpt-16 cpe-24 cpb-16 br-10">
        <div className="d-flex align-items-center gap-3 cmb-42">
          <i
            className="bi bi-chevron-left pointer"
            onClick={() => {
              navigate(-1);
            }}
          />
          <span className="text-16-500-20 color-title-navy">{userLabel}</span>
        </div>

        {!isLoading &&
          (listType === 'all' ? (
            <div
              className={`${
                user_type === '1'
                  ? 'justify-content-between'
                  : 'justify-content-end'
              } d-flex align-items-center cmb-30`}
            >
              {user_type === '1' && (
                <>
                  <div>
                    {/* <div className="d-flex align-items-center gap-3 cmb-26">
                    <p className="text-14-400-18 color-1717 m-0">
                      Call Integration
                    </p>
                    <ToggleSwitch
                      isChecked={call_integration === '1'}
                      onChange={handleCallToggle}
                    />
                  </div> */}

                    <div className="d-flex align-items-center gap-3">
                      <p className="text-14-400-18 color-1717 m-0">
                        {`Do you want to Data Masking for all ${titleCaseString(
                          type
                        )?.replace('-', ' ')}`}
                        ?
                      </p>
                      <ToggleSwitch
                        isChecked={data_masked === '1'}
                        onChange={handleToggle}
                      />
                    </div>
                  </div>
                </>
              )}

              {!isHide && updateAccess === '1' && (
                <div className="d-flex">
                  <Button
                    text={'Submit'}
                    className={'h-35 cps-15 cpe-15'}
                    btnStyle={'primary-dark'}
                    onClick={handleSubmit}
                    btnLoading={isSubmitLoading}
                  />
                </div>
              )}
            </div>
          ) : (
            <>
              <div className="d-flex gap-5 align-items-center">
                <Profile
                  text={name}
                  size="s-125"
                  url={profile_image}
                  isS3UserURL
                />
                <div className="d-flex flex-column ">
                  <div className="text-20-400-25 color-new-car cmb-14">
                    {name}
                  </div>
                  <div className="d-flex align-items-center gap-4 cmb-20">
                    {email_id && (
                      <div className="text-14-300-17 color-black-olive d-flex align-items-center gap-2">
                        <i className="bi bi-envelope" /> {email_id}
                      </div>
                    )}

                    {phone_number && (
                      <div className="text-14-300-17 color-black-olive d-flex align-items-center gap-2">
                        <i className="bi bi-telephone" />
                        {`${getCountryCode(country_code)} ${phone_number}`}
                      </div>
                    )}
                  </div>
                  <div className="d-flex flex-column flex-md-row gap-md-4 flex-wrap cmb-10">
                    {date_of_joining && (
                      <div>
                        <h6>Date of Joining</h6>
                        <p>{moment(date_of_joining).format('DD-MMM-YYYY')}</p>
                      </div>
                    )}
                    {member_id && (
                      <div>
                        <h6>Employee ID</h6>
                        <p>{member_id}</p>
                      </div>
                    )}
                    {(user_type === '1' || user_type === '0') && (
                      <div>
                        <h6>Data Masking</h6>
                        <ToggleSwitch
                          isChecked={data_masked === '1'}
                          onChange={handleToggle}
                        />
                      </div>
                    )}
                    {(user_type === '1' || user_type === '0') && (
                      <>
                        <div>
                          <h6>Call Integration</h6>
                          <ToggleSwitch
                            isChecked={call_integration === '1'}
                            onChange={handleCallToggle}
                          />
                        </div>
                      </>
                    )}
                    {(user_type === '1' || user_type === '0') && (
                      <div>
                        <h6>Export Details</h6>
                        <ToggleSwitch
                          isChecked={export_status === '1'}
                          onChange={handleExportDetailsToggle}
                        />
                      </div>
                    )}
                    {(user_type === '1' || user_type === '0') && (
                      <div className="">
                        <h6>Switch User</h6>
                        <Dropdown
                          // label="Switch User"
                          id="switch_user"
                          placeholder="Select User Type"
                          options={filteredOptions}
                          value={switchUserData?.newUserType}
                          // error={errors.membership_plan_id}
                          optionKey="id"
                          optionValue="title"
                          onChange={(e) => handleSwitchUserChange(e)}
                        />
                      </div>
                    )}
                  </div>

                  <div className="d-flex gap-5">
                    {(user_type === '1' || user_type === '0') && (
                      <>
                        {userData?.call_integration === '1' && (
                          <div className="cmb-5">
                            <h6>Agent Id</h6>
                            <div>
                              <TextInput
                                placeholder="Enter Agent Id"
                                id="call_hippo_agent_id"
                                onChange={handleAgentIdChange}
                                value={userData?.call_hippo_agent_id || ''}
                                error={errors?.call_hippo_agent_id}
                                required
                              />
                            </div>
                          </div>
                        )}

                        {userData?.call_integration === '1' && (
                          <div className="cmb-5">
                            <h6>Agent Phone No</h6>
                            <div>
                              <TextInput
                                placeholder="Enter Phone No"
                                id="call_hippo_agent_phone_no"
                                onChange={handleAgentPhoneNoChange}
                                value={
                                  userData?.call_hippo_agent_phone_no || ''
                                }
                                error={errors?.call_hippo_agent_phone_no}
                                required
                              />
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
              {!isHide && updateAccess === '1' && (
                <div className="d-flex cmb-30 justify-content-end">
                  <Button
                    text={
                      switchUserData?.newUserType && loginUserRole === '0'
                        ? 'Send Request'
                        : 'Submit'
                    }
                    className={'h-35 cps-15 cpe-15'}
                    btnStyle={'primary-dark'}
                    onClick={handleSubmit}
                    btnLoading={isSubmitLoading}
                    disabled={isSubmitLoading}
                  />
                </div>
              )}
            </>
          ))}

        <div className="w-100">
          <Table
            rowData={rowData}
            header={header}
            isLoading={isLoading}
            handleSelectAllChange={handleSelectAllChange}
            selectAllPermissions={selectAllPermissions}
          />
        </div>
      </Card>
    </>
  );
};

export default RBACUserListDetail;
